import React, { useState, useEffect } from 'react';
import { Auth } from 'aws-amplify';
import {
  Button,
  TextField,
  Typography,
  Alert,
  CircularProgress,
  Stepper,
  Step,
  StepLabel,
  Box,
} from '@mui/material';

/**
 * MfaSetup handles first-time MFA configuration for new users.
 * Flow:
 * 1. Verify phone number for SMS MFA (required)
 * 2. Setup complete — user can optionally configure an authenticator app
 *    later from the Authenticator tab in the portal.
 *
 * A user is considered valid/confirmed when both email and phone are verified.
 * If the user quits partway, the next login will detect incomplete setup
 * and resume from the appropriate step.
 */

const STEPS = ['Verify Phone (SMS)', 'Complete'];

function MfaSetup({ userSession, onSetupComplete }) {
  const [activeStep, setActiveStep] = useState(0);
  const [errorMessage, setErrorMessage] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [loading, setLoading] = useState(false);

  // Phone verification state
  const [phoneNumber, setPhoneNumber] = useState('');
  const [smsCode, setSmsCode] = useState('');
  const [smsSent, setSmsSent] = useState(false);
  const [smsVerified, setSmsVerified] = useState(false);

  // Check existing verification status on mount
  useEffect(() => {
    checkVerificationStatus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const checkVerificationStatus = async () => {
    try {
      const user = userSession || await Auth.currentAuthenticatedUser();
      const attributes = user.attributes || user.challengeParam?.userAttributes || {};

      const phoneVerified = attributes.phone_number_verified === 'true' || attributes.phone_number_verified === true;

      if (phoneVerified) {
        setSmsVerified(true);
        // Phone already verified, go straight to complete
        setActiveStep(1);
      }
      // If phone is not verified, stay on step 0 (default)
    } catch (error) {
      console.error('Error checking verification status:', error);
    }
  };

  // --- SMS / Phone Verification ---
  const handleSendSmsCode = async (e) => {
    e.preventDefault();
    setErrorMessage('');
    setSuccessMessage('');
    setLoading(true);

    try {
      const user = await Auth.currentAuthenticatedUser();

      // Format phone number (ensure it starts with +)
      let formattedPhone = phoneNumber.trim();
      if (!formattedPhone.startsWith('+')) {
        formattedPhone = `+${formattedPhone}`;
      }

      // Update phone number attribute
      await Auth.updateUserAttributes(user, {
        phone_number: formattedPhone,
      });

      // Request verification code
      await Auth.verifyUserAttribute(user, 'phone_number');

      setSmsSent(true);
      setSuccessMessage('Verification code sent to your phone.');
    } catch (error) {
      console.error('Send SMS error:', error);
      setErrorMessage(error.message || 'Failed to send verification code.');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifySms = async (e) => {
    e.preventDefault();
    setErrorMessage('');
    setSuccessMessage('');
    setLoading(true);

    try {
      const user = await Auth.currentAuthenticatedUser();
      await Auth.verifyUserAttributeSubmit(user, 'phone_number', smsCode);

      // Enable SMS MFA
      await Auth.setPreferredMFA(user, 'SMS');

      setSmsVerified(true);
      setSuccessMessage('Phone number verified and SMS MFA enabled!');
      setActiveStep(1);
    } catch (error) {
      console.error('Verify SMS error:', error);
      if (error.code === 'CodeMismatchException') {
        setErrorMessage('Invalid verification code.');
      } else {
        setErrorMessage(error.message || 'Verification failed. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleComplete = () => {
    if (onSetupComplete) {
      onSetupComplete();
    } else {
      window.location.href = '/';
    }
  };

  const renderPhoneStep = () => (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
      <Typography variant="body2" style={{ textAlign: 'center' }}>
        Verify your phone number for SMS-based multi-factor authentication.
      </Typography>

      {!smsSent ? (
        <form onSubmit={handleSendSmsCode} style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
          <TextField
            label="Phone Number"
            variant="outlined"
            value={phoneNumber}
            onChange={(e) => setPhoneNumber(e.target.value)}
            placeholder="+64 21 123 4567"
            helperText="Include country code (e.g., +64 for NZ)"
            required
            disabled={loading}
          />
          <Button type="submit" color="primary" variant="contained" size="large" disabled={loading}>
            {loading ? <CircularProgress size={24} /> : 'Send Verification Code'}
          </Button>
        </form>
      ) : (
        <form onSubmit={handleVerifySms} style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
          <TextField
            label="SMS Verification Code"
            variant="outlined"
            value={smsCode}
            onChange={(e) => setSmsCode(e.target.value)}
            required
            disabled={loading}
          />
          <Button type="submit" color="primary" variant="contained" size="large" disabled={loading}>
            {loading ? <CircularProgress size={24} /> : 'Verify Phone'}
          </Button>
          <Button
            onClick={() => { setSmsSent(false); setSmsCode(''); }}
            color="inherit"
            size="small"
          >
            Resend Code
          </Button>
        </form>
      )}
    </div>
  );

  const renderCompleteStep = () => (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '15px', textAlign: 'center' }}>
      <Typography variant="h6">Setup Complete!</Typography>
      <Typography variant="body2">
        Your account is now secured with SMS multi-factor authentication.
      </Typography>
      <Alert severity="success">
        SMS MFA is configured. You can set up an authenticator app for enhanced security from the
        Authenticator tab after logging in.
      </Alert>
      <Button onClick={handleComplete} color="primary" variant="contained" size="large">
        Continue to Portal
      </Button>
    </div>
  );

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '15px', width: '320px' }}>
      <Typography variant="h6" style={{ textAlign: 'center' }}>
        Account Setup
      </Typography>

      <Stepper activeStep={activeStep} alternativeLabel>
        {STEPS.map((label) => (
          <Step key={label}>
            <StepLabel>{label}</StepLabel>
          </Step>
        ))}
      </Stepper>

      {errorMessage && (
        <Alert severity="error">{errorMessage}</Alert>
      )}
      {successMessage && (
        <Alert severity="success">{successMessage}</Alert>
      )}

      <Box sx={{ mt: 2 }}>
        {activeStep === 0 && renderPhoneStep()}
        {activeStep === 1 && renderCompleteStep()}
      </Box>
    </div>
  );
}

export default MfaSetup;
